import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, MapPin, Search, ArrowLeft } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Card, Pill, Spinner, EmptyState, Input, Select } from "@/components/ui";

// =============================================================================
// SitesScreen — /sites
// Directory of every physical location Coretel has worked or is tendering on.
// Search + active/inactive filter. Row → /sites/:siteId. T-037.
// =============================================================================

interface SiteRow {
  site_id:               string;
  site_code:             string;
  site_name:             string;
  suburb:                string | null;
  state:                 string | null;
  postcode:              string | null;
  rfnsa_id:              string | null;
  site_type:             string | null;
  structure_height_m:    number | null;
  is_active:             boolean;
  asset_owner:           { owner_code: string } | null;
}

type StatusFilter = "active" | "inactive" | "all";

export default function SitesScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");

  const canCreate = user?.role === "director" || user?.role === "manager";

  const { data: sites, isLoading, error } = useQuery({
    queryKey: ["sites_list", statusFilter],
    queryFn: async () => {
      let q = supabase
        .from("sites")
        .select("site_id, site_code, site_name, suburb, state, postcode, rfnsa_id, site_type, structure_height_m, is_active, asset_owner:asset_owners(owner_code)")
        .order("site_code", { ascending: false });

      if (statusFilter === "active") q = q.eq("is_active", true);
      else if (statusFilter === "inactive") q = q.eq("is_active", false);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as SiteRow[];
    },
  });

  const filtered = useMemo(() => {
    if (!sites) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return sites;
    return sites.filter(s =>
      s.site_code.toLowerCase().includes(needle) ||
      s.site_name.toLowerCase().includes(needle) ||
      (s.suburb ?? "").toLowerCase().includes(needle) ||
      (s.rfnsa_id ?? "").toLowerCase().includes(needle)
    );
  }, [sites, search]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Link
        to="/project"
        className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"
      >
        <ArrowLeft size={14}/> Project portal
      </Link>

      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-[20px] font-semibold text-text flex items-center gap-2">
            <MapPin size={20} className="text-accent" />
            Sites
          </h1>
          <div className="text-[12px] text-text-muted mt-0.5">
            Physical locations — linked to projects and BIDs. {sites?.length ?? 0} total.
          </div>
        </div>
        {canCreate && (
          <Button icon={Plus} onClick={() => navigate("/sites/new")}>
            New site
          </Button>
        )}
      </div>

      <Card>
        <div className="p-4 border-b border-line flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by code, name, suburb, RFNSA ID"
              className="pl-8"
            />
          </div>
          <Select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as StatusFilter)}
            className="max-w-[160px]"
          >
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
            <option value="all">All</option>
          </Select>
        </div>

        {isLoading && (
          <div className="p-8 flex justify-center"><Spinner /></div>
        )}

        {error && (
          <div className="p-6 text-[13px] text-red-400">
            Failed to load sites: {error instanceof Error ? error.message : String(error)}
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <EmptyState
            icon={MapPin}
            title={search ? "No sites match that search" : "No sites yet"}
            description={canCreate ? "Create the first site to link projects and BIDs against a real location." : "A director or manager will add sites as work comes in."}
          />
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-text-muted border-b border-line">
                  <th className="px-4 py-2 font-medium">Code</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Owner</th>
                  <th className="px-4 py-2 font-medium">Location</th>
                  <th className="px-4 py-2 font-medium">RFNSA</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium text-right">Height</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const loc = [s.suburb, s.state, s.postcode].filter(Boolean).join(" ");
                  return (
                    <tr
                      key={s.site_id}
                      className="border-b border-line/50 hover:bg-ink-raised/50 cursor-pointer"
                      onClick={() => navigate(`/sites/${s.site_id}`)}
                    >
                      <td className="px-4 py-2.5 font-mono text-[12px] text-accent">{s.site_code}</td>
                      <td className="px-4 py-2.5 text-text">
                        <Link to={`/sites/${s.site_id}`} onClick={e => e.stopPropagation()} className="hover:underline">
                          {s.site_name}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5 text-text-muted font-mono text-[12px]">{s.asset_owner?.owner_code || <span className="text-text-dim">—</span>}</td>
                      <td className="px-4 py-2.5 text-text-muted">{loc || <span className="text-text-dim">—</span>}</td>
                      <td className="px-4 py-2.5 text-text-muted font-mono text-[12px]">{s.rfnsa_id || <span className="text-text-dim">—</span>}</td>
                      <td className="px-4 py-2.5 text-text-muted">{s.site_type || <span className="text-text-dim">—</span>}</td>
                      <td className="px-4 py-2.5 text-right text-text-muted font-mono text-[12px]">
                        {s.structure_height_m != null
                          ? `${Number(s.structure_height_m).toFixed(1)} m`
                          : <span className="text-text-dim">—</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <Pill label={s.is_active ? "Active" : "Inactive"} tone={s.is_active ? "ok" : "default"} small />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
